import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, catchError, of, forkJoin } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from "../../environments/environment";

export interface ExclusiveDiscountItem {
  id: number;
  titulo: string;
  img: string;
  url: string;
  orden: number;
  ancho: number;
}

export interface ExclusiveDiscountsResponse {
  tipoDispositivo: {
    xl: ExclusiveDiscountItem[];
    desktop: ExclusiveDiscountItem[];
    tablet: ExclusiveDiscountItem[];
    movil: ExclusiveDiscountItem[];
  };
}

export interface ExclusiveDiscounts {
  unimarc: ExclusiveDiscountItem[];
  alvi: ExclusiveDiscountItem[];
  mayorista: ExclusiveDiscountItem[];
}

@Injectable({
  providedIn: 'root'
})
export class ExclusiveDiscountsService {
  private readonly baseUrl = environment.apiUrl+'/catalogos';
  private cachedResponses: { [key: string]: ExclusiveDiscountsResponse } = {};
  private lastFetchTimes: { [key: string]: number } = {};
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutos
  private isLoading: { [key: string]: boolean } = {};
  private currentRequests: { [key: string]: Observable<ExclusiveDiscountsResponse> } = {};
  private getAllDiscountsRequest$: Observable<ExclusiveDiscounts> | null = null;

  constructor(private readonly http: HttpClient) {}

  // Método para obtener descuentos de una marca específica
  getDiscountsByBrand(brand: 'unimarc' | 'alvi' | 'mayorista'): Observable<ExclusiveDiscountsResponse> {
    const apiUrl = `${this.baseUrl}/${brand}`;
    const now = Date.now();

    // Si ya hay una petición en curso para esta marca, retornar la misma
    if (this.isLoading[brand] && this.currentRequests[brand]) {
      return this.currentRequests[brand]!;
    }

    // Retornar cache si está disponible y no ha expirado
    if (this.cachedResponses[brand] && (now - (this.lastFetchTimes[brand] || 0)) < this.CACHE_DURATION) {
      return of(this.cachedResponses[brand]!);
    }

    // Si hay cache expirado, retornarlo mientras se hace nueva petición
    if (this.cachedResponses[brand]) {
      return of(this.cachedResponses[brand]!);
    }

    // Hacer nueva llamada
    console.log(`🔄 Descuentos ${brand}: Iniciando nueva petición HTTP`);
    this.isLoading[brand] = true;

    this.currentRequests[brand] = this.http.get<ExclusiveDiscountsResponse>(apiUrl).pipe(
      tap(response => {
        console.log(`✅ Descuentos ${brand}: Petición HTTP completada`);
        this.cachedResponses[brand] = response;
        this.lastFetchTimes[brand] = Date.now();
        this.isLoading[brand] = false;
        delete this.currentRequests[brand];
      }),
      catchError(error => {
        console.error(`❌ Descuentos ${brand}: Error en petición HTTP:`, error);
        this.isLoading[brand] = false;
        delete this.currentRequests[brand];

        // Retornar cache anterior si existe, aunque haya expirado
        if (this.cachedResponses[brand]) {
          return of(this.cachedResponses[brand]!);
        }
        throw error;
      }),
      shareReplay(1) // Compartir el resultado con múltiples suscriptores
    );

    return this.currentRequests[brand]!;
  }

  // Método para obtener descuentos de una marca específica para un tipo de dispositivo
  getDiscountsByBrandAndDevice(brand: 'unimarc' | 'alvi' | 'mayorista', deviceType: 'xl' | 'desktop' | 'tablet' | 'movil'): Observable<ExclusiveDiscountItem[]> {
    return this.getDiscountsByBrand(brand).pipe(
      map(response => response.tipoDispositivo[deviceType] || [])
    );
  }

    // Método para obtener todos los descuentos (combinando las 3 marcas)
  getAllDiscounts(deviceType?: 'xl' | 'desktop' | 'tablet' | 'movil'): Observable<ExclusiveDiscounts> {
    // Si ya hay una petición en curso, retornar la misma
    if (this.getAllDiscountsRequest$) {
      return this.getAllDiscountsRequest$;
    }

    const currentDevice = deviceType || this.getCurrentDeviceType();
    const now = Date.now();

    // Verificar si ya tenemos cache para todas las marcas
    const hasAllCache = this.cachedResponses['unimarc'] &&
                       this.cachedResponses['alvi'] &&
                       this.cachedResponses['mayorista'];

    const allCacheValid = hasAllCache &&
                         (now - (this.lastFetchTimes['unimarc'] || 0)) < this.CACHE_DURATION &&
                         (now - (this.lastFetchTimes['alvi'] || 0)) < this.CACHE_DURATION &&
                         (now - (this.lastFetchTimes['mayorista'] || 0)) < this.CACHE_DURATION;

    // Si tenemos cache válido para todas las marcas, retornar inmediatamente
    if (allCacheValid) {
      return of({
        unimarc: this.cachedResponses['unimarc']!.tipoDispositivo[currentDevice] || [],
        alvi: this.cachedResponses['alvi']!.tipoDispositivo[currentDevice] || [],
        mayorista: this.cachedResponses['mayorista']!.tipoDispositivo[currentDevice] || []
      });
    }

    // Si tenemos cache expirado para todas las marcas, retornar cache expirado
    if (hasAllCache) {
      return of({
        unimarc: this.cachedResponses['unimarc']!.tipoDispositivo[currentDevice] || [],
        alvi: this.cachedResponses['alvi']!.tipoDispositivo[currentDevice] || [],
        mayorista: this.cachedResponses['mayorista']!.tipoDispositivo[currentDevice] || []
      });
    }

    // Solo hacer forkJoin si realmente necesitamos hacer llamadas HTTP
    this.getAllDiscountsRequest$ = forkJoin({
      unimarc: this.getDiscountsByBrand('unimarc'),
      alvi: this.getDiscountsByBrand('alvi'),
      mayorista: this.getDiscountsByBrand('mayorista')
    }).pipe(
      map(responses => {
        return {
          unimarc: responses.unimarc.tipoDispositivo[currentDevice] || [],
          alvi: responses.alvi.tipoDispositivo[currentDevice] || [],
          mayorista: responses.mayorista.tipoDispositivo[currentDevice] || []
        };
      }),
      tap(() => {
        // Limpiar la referencia después de completar
        setTimeout(() => {
          this.getAllDiscountsRequest$ = null;
        }, 1000); // Esperar 1 segundo antes de limpiar
      }),
      shareReplay(1) // Compartir el resultado con múltiples suscriptores
    );

    return this.getAllDiscountsRequest$;
  }

  // Método para obtener descuentos de una marca específica para todos los dispositivos
  getDiscountsByBrandAllDevices(brand: 'unimarc' | 'alvi' | 'mayorista'): Observable<{
    xl: ExclusiveDiscountItem[];
    desktop: ExclusiveDiscountItem[];
    tablet: ExclusiveDiscountItem[];
    movil: ExclusiveDiscountItem[];
  }> {
    return this.getDiscountsByBrand(brand).pipe(
      map(response => response.tipoDispositivo)
    );
  }

  getCurrentDeviceType(): 'xl' | 'desktop' | 'tablet' | 'movil' {
    const width = window.innerWidth;

    if (width >= 1200) return 'xl';
    if (width >= 769) return 'desktop'; // Cambiado de 992 a 769 para que coincida con el breakpoint
    if (width >= 768) return 'tablet';
    return 'desktop'; // Corregido: dispositivos menores a 768px son móviles
  }

  // Método para limpiar cache (útil para testing o actualizaciones forzadas)
  clearCache(): void {
    console.log('🧹 Descuentos: Limpiando cache de todas las marcas');
    this.cachedResponses = {};
    this.lastFetchTimes = {};
    this.isLoading = {};
    this.currentRequests = {};
  }

  // Método para limpiar cache de una marca específica
  clearCacheForBrand(brand: 'unimarc' | 'alvi' | 'mayorista'): void {
    console.log(`🧹 Descuentos: Limpiando cache de ${brand}`);
    delete this.cachedResponses[brand];
    delete this.lastFetchTimes[brand];
    delete this.isLoading[brand];
    delete this.currentRequests[brand];
  }

  // Método para forzar actualización (ignora cache)
  forceRefresh(deviceType?: 'xl' | 'desktop' | 'tablet' | 'movil'): Observable<ExclusiveDiscounts> {
    console.log('🔄 Descuentos: Forzando actualización de todas las marcas');
    this.clearCache();
    return this.getAllDiscounts(deviceType);
  }

  // Método para forzar actualización de una marca específica
  forceRefreshBrand(brand: 'unimarc' | 'alvi' | 'mayorista'): Observable<ExclusiveDiscountsResponse> {
    console.log(`🔄 Descuentos: Forzando actualización de ${brand}`);
    this.clearCacheForBrand(brand);
    return this.getDiscountsByBrand(brand);
  }

  // Método para verificar estado del cache
  getCacheStatus(): {
    hasCache: boolean;
    isExpired: boolean;
    isLoading: boolean;
    brands: { [key: string]: { hasCache: boolean; isExpired: boolean; isLoading: boolean } }
  } {
    const now = Date.now();
    const brands = ['unimarc', 'alvi', 'mayorista'] as const;
    const brandStatus: { [key: string]: { hasCache: boolean; isExpired: boolean; isLoading: boolean } } = {};

    let hasAnyCache = false;
    let hasAnyExpired = false;
    let hasAnyLoading = false;

    brands.forEach(brand => {
      const hasCache = !!this.cachedResponses[brand];
      const isExpired = hasCache && (now - (this.lastFetchTimes[brand] || 0)) >= this.CACHE_DURATION;
      const isLoading = !!this.isLoading[brand];

      brandStatus[brand] = { hasCache, isExpired, isLoading };

      if (hasCache) hasAnyCache = true;
      if (isExpired) hasAnyExpired = true;
      if (isLoading) hasAnyLoading = true;
    });

    return {
      hasCache: hasAnyCache,
      isExpired: hasAnyExpired,
      isLoading: hasAnyLoading,
      brands: brandStatus
    };
  }

  // Método para obtener estadísticas de descuentos por marca
  getDiscountsStatistics(): Observable<{
    unimarc: { total: number; byDevice: { [key: string]: number } };
    alvi: { total: number; byDevice: { [key: string]: number } };
    mayorista: { total: number; byDevice: { [key: string]: number } };
  }> {
    return forkJoin({
      unimarc: this.getDiscountsByBrand('unimarc'),
      alvi: this.getDiscountsByBrand('alvi'),
      mayorista: this.getDiscountsByBrand('mayorista')
    }).pipe(
      map(responses => {
        const stats = {
          unimarc: { total: 0, byDevice: {} as { [key: string]: number } },
          alvi: { total: 0, byDevice: {} as { [key: string]: number } },
          mayorista: { total: 0, byDevice: {} as { [key: string]: number } }
        };

        const deviceTypes: ('xl' | 'desktop' | 'tablet' | 'movil')[] = ['xl', 'desktop', 'tablet', 'movil'];

        deviceTypes.forEach(deviceType => {
          const unimarcCount = (responses.unimarc.tipoDispositivo[deviceType] || []).length;
          const alviCount = (responses.alvi.tipoDispositivo[deviceType] || []).length;
          const mayoristaCount = (responses.mayorista.tipoDispositivo[deviceType] || []).length;

          stats.unimarc.byDevice[deviceType] = unimarcCount;
          stats.alvi.byDevice[deviceType] = alviCount;
          stats.mayorista.byDevice[deviceType] = mayoristaCount;

          stats.unimarc.total += unimarcCount;
          stats.alvi.total += alviCount;
          stats.mayorista.total += mayoristaCount;
        });

        return stats;
      })
    );
  }
}
